<template>
    <div class="documents-component" data-cy="profile-view-documents">
        <div class="left" v-if="isProfileView">
            <div data-cy="profile-view-data-privacy" class="data-privacy-message"><InformationCircleIcon class="hero-icon" /> {{t("documents.headline8")}}</div>
        </div>
        <div class="left" v-else-if="!hideConsent && (!isDirectSignUp || candidate.brandId === BrandId.BigFour)">
            <h5>{{t("documents.headline")}}</h5>
        </div>
        <div class="right">
            <div class="box" v-if="isViewing" @click="downloadCV">
                <div data-cy="profile-view-download-cv" v-if="hasCV" class="info-box" style="cursor: pointer">
                    <img :src="cvIcon" />
                    <span data-cy="cv-file-name">{{ displayFriendlyFileName }}</span>
                </div>
                <div v-else>
                    <div class="info-box">
                        {{t("documents.headline2")}}
                    </div>
                    <Button data-cy="upload-cv" class="fake-upload-button button-disabled">{{t("documents.headline3")}}<CloudUploadIcon /></Button>
                </div>

            </div>
            <div v-else class="box">
                <div v-if="isUploading">
                    <ProgressBar mode="determinate" :value="uploadProgress" />
                </div>
                <div v-else-if="!isUploading && hasCV" class="info-box">
                    <img :src="cvIcon" />
                    <span data-cy="cv-file-name">{{ displayFriendlyFileName }}</span>
                    <!-- TODO: display-friendly Filename, eg. SurnameFirstname_CV -->

                </div>
                <div v-else class="info-box">
                    {{t("documents.headline4")}}
                </div>

                <FileUpload :auto="true"
                            mode="basic"
                            name="cv"
                            data-cy="fileupload-choose"
                            :url="uploadUrl"
                            @progress="handleUploadProgress($event)"
                            @before-upload="isUploading = true"
                            @upload="onSuccessfulUpload()"
                            @error="onFailedUpload()"
                            :customUpload="true"
                            @uploader="uploadHandler($event)"
                            :multiple="false"
                            accept=".pdf, .doc, .docx"
                            :maxFileSize="20000000"
                            :chooseLabel="uploadLabel">
                    <template #empty>
                        <p>{{t("documents.headline6")}}</p>
                    </template>
                </FileUpload>
                <p v-if="cvIsMandatoryMessage || props.showCvIsMandatoryMessage" class="p-error">
                    {{t("documents.headline7")}}
                </p>
                <p v-if="uploadSuccess" data-cy="profile-edit-upload-success">{{t("documents.headline5")}}</p>
            </div>
            <div data-cy="data-privacy" class="data-privacy-message" v-if="!hideConsent && !isProfileView">{{t("documents.headline8")}}</div>
            <div class="call-to-action mobile-bottom-nav" v-if="showButtons">
                <Button data-cy="button-profile-prev"
                        class="button-secondary button-back p-pl-3"
                        @click="prev()">
                    <i class="pi pi-angle-left" />{{t("back")}}
                </Button>
                <Button data-cy="button-profile-next"
                        class="button-primary button-next p-pr-3"
                        @click="next('submit')"
                        v-if="currentBrandId === BrandId.BigFour">
                    {{(isDirectSignUp ? t("continue") : t("submit")) }} <i class="pi pi-angle-right p-ml-1" />
                </Button>
                <Button data-cy="button-profile-next"
                        class="button-rectangle button-primary button-next"
                        @click="next('next')"
                        v-else>
                    {{t("continue")}} <i class="pi pi-angle-right" />
                </Button>
            </div>
        </div>
    </div>
</template>

<script lang="ts" setup>
    import { computed, defineEmits, defineProps, getCurrentInstance, ref, } from "vue";
    import { CandidateProfile } from "@/models/CandidateProfile";
    import { EncCandidateProfile } from "@/models/EncCandidateProfile";
    import { BrandId } from "@/models/BrandIds";
    import ProgressBar from "primevue/progressbar";
    import FileUpload from "primevue/fileupload";
    import { useToast } from "primevue/usetoast";
    import { createProfileService } from "@/services/profileService";
    import { createSignupService } from "@/services/signupService";
    import { CloudUploadIcon, InformationCircleIcon } from '@heroicons/vue/solid';
    import { useI18n } from "vue-i18n";

    const emit = defineEmits(["cvUploaded", "submit", "previous"]);
    const props = defineProps<{
        isViewing: boolean;
        isProfileView: boolean;
        candidate: CandidateProfile | EncCandidateProfile;
        showButtons: boolean;
        disableValidation: boolean;
        isDirectSignUp: boolean;
        currentBrandId: BrandId;
        hideConsent: boolean;
        showCvIsMandatoryMessage: boolean;
    }>();

    const editableCandidate = ref(props.candidate);
    const app = getCurrentInstance();
    const { t } = useI18n();
    const toaster = useToast();

    const hasCV = computed(() => {
        if (editableCandidate.value?.latestCV) {
            return true;
        }
        return false;
    });

    const latestCV = computed(() => {
        return editableCandidate.value.latestCV;
    });

    const cvIcon = ref("");

    const getCvIcon = (filename) => {
        if (filename) {
            if (filename.indexOf(".pdf") > -1) {
                return "/kandidaten/img/pdf.svg";
            } else if (filename.indexOf(".doc") > -1) {
                return "/kandidaten/img/doc.svg";
            } else if (filename.indexOf(".docx") > -1) {
                return "/kandidaten/img/docx.svg";
            } else {
                return "";
            }
        }
        return "";
    }

    const isUploading = ref<Boolean>(false);
    const uploadProgress = ref();

    const uploadLabel = computed(() => {
        return isUploading.value ? "Processing" : t("documents.headline10");
    });

    const uploadUrl = ref("/kandidaten/profile/cv/" + props.candidate.id);
    const uploadSuccess = ref(false);
    const cvIsMandatoryMessage = ref(false);

    const onSuccessfulUpload = async () => {
        await resetUpload();
        toaster.add({
            severity: "success",
            summary: t("documents.headline9"),
            detail: "",
            life: 3000,
        });
        uploadSuccess.value = true;
        cvIsMandatoryMessage.value = false;
        hasCV.value = true;
        emit("cvUploaded");
    };

    const onFailedUpload = async () => {
        await resetUpload();
        toaster.add({
            severity: "error",
            summary: t("documents.upload.error"),
            detail: "",
            life: 3000,
        });
    };

    const resetUpload = async () => {
        isUploading.value = false;
        uploadProgress.value = 0;
    };

    const handleUploadProgress = async (event) => {
        uploadProgress.value = event.progress;
    };

    const submitted = ref(false);
    const uploadHandler = async (event) => {
        submitted.value = true;
        isUploading.value = true;
        const service = props.isDirectSignUp ? await createSignupService() : await createProfileService(app);
        let id = props.isDirectSignUp ? editableCandidate.value.encryptedId : editableCandidate.value.id;
        const result = await service.postCV(
            event.files[0],
            id,
            (event) =>
            (uploadProgress.value = Math.min(
                Math.ceil((event.loaded / event.total) * 100),
                100
            ))
        );
        cvIcon.value = getCvIcon(event.files[0].name);
        isUploading.value = false;
        if (result) {
            await onSuccessfulUpload();
        } else {
            await onFailedUpload();
        }
    };

    const displayFriendlyFileName = computed(() => {
        try {
            return `${editableCandidate.value.lastName}${editableCandidate.value.firstName}_CV`
        } catch (error) {
            return latestCV.value.fileName;
        }
    });

    const downloadCV = async () => {

        const service = await createProfileService(app);
        const split = latestCV.value.fileName.split(".");

        if (!split || split.length < 2) {
            throw new Error(t("documents.file.name.extension.error"));
        }

        const name = displayFriendlyFileName.value != "" ? displayFriendlyFileName.value : split[0];

        await service.downloadCv(name, split[1]);



    };

    const next = (emitVal) => {
        if (uploadSuccess.value || props.disableValidation) {
            emit(emitVal, editableCandidate.value);
            cvIsMandatoryMessage.value = false;
        } else {
            cvIsMandatoryMessage.value = true;
        }
    };

    const prev = () => {
        emit("previous", editableCandidate.value);
    };
</script>
